import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import {
  Mic, MicOff, Send, Loader2, CheckCircle, AlertCircle,
  Trophy, BookOpen, MessageSquare, Sparkles, ArrowRight, Star
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const useSpeechRecognition = (onTranscript) => {
  const recognitionRef = useRef(null);
  const [listening, setListening] = useState(false);
  const [supported, setSupported] = useState(false);

  useEffect(() => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) return;
    setSupported(true);
    const r = new SR();
    r.continuous = true;
    r.interimResults = false;
    r.lang = 'en-IN';
    r.onresult = (e) => {
      let transcript = '';
      for (let i = e.resultIndex; i < e.results.length; i++) {
        transcript += e.results[i][0].transcript + ' ';
      }
      onTranscript(transcript.trim());
    };
    r.onerror = () => setListening(false);
    r.onend = () => setListening(false);
    recognitionRef.current = r;
    return () => { try { r.stop(); } catch (e) { /* noop */ } };
  }, [onTranscript]);

  const toggle = () => {
    const r = recognitionRef.current;
    if (!r) return;
    if (listening) {
      try { r.stop(); } catch (e) { /* noop */ }
      setListening(false);
    } else {
      try { r.start(); setListening(true); } catch (e) { /* noop */ }
    }
  };

  return { listening, supported, toggle };
};

const AIInterviewSession = () => {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [answer, setAnswer] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [latestFeedback, setLatestFeedback] = useState(null);
  const [error, setError] = useState('');
  const scrollRef = useRef(null);

  const appendToAnswer = useCallback((text) => {
    setAnswer((prev) => (prev ? prev + ' ' + text : text));
  }, []);
  const { listening, supported, toggle } = useSpeechRecognition(appendToAnswer);

  // Initial load
  useEffect(() => {
    document.title = 'AI Mock Interview — Vidya365';
    const token = localStorage.getItem('studentToken');
    if (!token) {
      navigate(`/student/login?redirect=/ai-interview/session/${sessionId}`);
      return;
    }
    fetch(`${API_URL}/api/interview/session/${sessionId}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => (r.ok ? r.json() : Promise.reject('not found')))
      .then((data) => setSession(data))
      .catch(() => setError('Could not load interview session.'))
      .finally(() => setLoading(false));
  }, [sessionId, navigate]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [session, latestFeedback]);

  const submit = async () => {
    if (!answer.trim() || submitting) return;
    setSubmitting(true);
    setLatestFeedback(null);
    const token = localStorage.getItem('studentToken');
    const headers = { 'Content-Type': 'application/json' };
    if (token) headers.Authorization = `Bearer ${token}`;
    try {
      const res = await fetch(`${API_URL}/api/interview/answer`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ session_id: sessionId, answer: answer.trim() }),
      });
      if (!res.ok) throw new Error('Submit failed');
      const data = await res.json();

      // Refresh full session state
      const fresh = await fetch(`${API_URL}/api/interview/session/${sessionId}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      }).then((r) => r.json());
      setSession(fresh);
      setLatestFeedback(data.feedback);
      setAnswer('');
      if (listening) toggle();
    } catch (e) {
      alert('Failed to submit answer. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="py-20 text-center"><Loader2 className="animate-spin mx-auto text-indigo-600" size={48} /></div>
      </div>
    );
  }

  if (error || !session) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="py-20 text-center">
          <AlertCircle className="mx-auto text-red-600 mb-3" size={48} />
          <p className="text-gray-700 mb-4">{error || 'Session not found'}</p>
          <Link to="/ai-interview"><Button>Back to AI Interview</Button></Link>
        </div>
      </div>
    );
  }

  const completed = session.status === 'completed';
  const current = session.qa_history[session.current_q_index];
  const isWaitingAnswer = !completed && current && !current.answer;
  const total = 10;
  const progressPct = Math.round((session.current_q_index / total) * 100);

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      <section className="container mx-auto px-4 py-6 max-w-4xl">
        {/* Header bar */}
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <div>
            <Badge className="bg-indigo-100 text-indigo-700 mb-1">{session.exam_name}</Badge>
            <h1 className="text-xl font-bold" data-testid="session-title">AI Mock Interview</h1>
          </div>
          <Link to="/ai-interview" className="text-sm text-indigo-600 hover:underline">← Back</Link>
        </div>

        {/* Progress */}
        <div className="bg-white rounded-lg p-4 mb-4 border" data-testid="progress-bar">
          <div className="flex justify-between text-xs text-gray-600 mb-1">
            <span>Question {Math.min(session.current_q_index + 1, total)} of {total}</span>
            <span>{progressPct}% complete</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div className="bg-gradient-to-r from-indigo-600 to-purple-600 h-2 rounded-full transition-all" style={{ width: `${progressPct}%` }} />
          </div>
        </div>

        {/* Conversation thread */}
        <div ref={scrollRef} className="bg-white rounded-lg border max-h-[55vh] overflow-y-auto p-4 mb-4 space-y-4" data-testid="conversation-thread">
          {session.qa_history.map((qa, i) => (
            <div key={i}>
              {/* Question bubble */}
              <div className="flex items-start gap-3 mb-2">
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-indigo-600 to-purple-700 flex items-center justify-center text-white text-xs font-bold flex-shrink-0">AI</div>
                <div className="flex-1">
                  <p className="text-xs text-gray-500 mb-1">Question {i + 1}</p>
                  <div className="bg-indigo-50 border border-indigo-100 rounded-lg p-3 text-gray-800">{qa.question}</div>
                </div>
              </div>
              {/* Answer bubble */}
              {qa.answer && (
                <div className="flex items-start gap-3 mb-2 flex-row-reverse">
                  <div className="w-8 h-8 rounded-full bg-gray-700 flex items-center justify-center text-white text-xs font-bold flex-shrink-0">You</div>
                  <div className="flex-1 max-w-[85%]">
                    <div className="bg-gray-100 rounded-lg p-3 text-gray-800 text-sm">{qa.answer}</div>
                  </div>
                </div>
              )}
              {/* Feedback */}
              {qa.feedback && (
                <div className="ml-11 bg-amber-50 border border-amber-200 rounded-lg p-3 mt-2" data-testid={`feedback-${i}`}>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-bold text-amber-900">AI Feedback</span>
                    <Badge className="bg-amber-600 text-white">
                      <Star className="w-3 h-3 mr-1 fill-current" /> {qa.feedback.score}/10
                    </Badge>
                  </div>
                  <p className="text-xs text-gray-700 mb-1"><strong className="text-green-700">✓ Good:</strong> {qa.feedback.strengths}</p>
                  <p className="text-xs text-gray-700 mb-1"><strong className="text-red-700">↑ Improve:</strong> {qa.feedback.improvements}</p>
                  <p className="text-xs text-gray-700"><strong className="text-indigo-700"><BookOpen className="w-3 h-3 inline mr-1" />Model:</strong> {qa.feedback.model_answer}</p>
                </div>
              )}
            </div>
          ))}

          {submitting && (
            <div className="flex items-center gap-2 text-indigo-600">
              <Loader2 className="animate-spin" size={16} /> Scoring your answer & preparing next question...
            </div>
          )}
        </div>

        {/* Final Report */}
        {completed && session.final_report && (
          <Card className="mb-4 border-2 border-indigo-400 bg-gradient-to-br from-indigo-50 to-purple-50" data-testid="final-report">
            <CardContent className="p-5">
              <div className="text-center mb-4">
                <Trophy className="mx-auto mb-2 text-yellow-600" size={36} />
                <h2 className="text-xl font-bold">Interview Complete!</h2>
                <div className="flex justify-center items-center gap-2 mt-2">
                  <span className="text-3xl font-bold text-indigo-700">{session.final_report.overall_score}</span>
                  <span className="text-gray-600">/ 100</span>
                  <Badge className="bg-indigo-600 text-white ml-2">{session.final_report.verdict}</Badge>
                </div>
              </div>
              <div className="grid md:grid-cols-2 gap-3 mb-3">
                <div className="bg-white p-3 rounded-lg border border-green-200">
                  <h3 className="font-bold text-green-700 text-sm mb-2">✓ Key Strengths</h3>
                  <ul className="text-xs text-gray-700 space-y-1">
                    {(session.final_report.key_strengths || []).map((s, i) => <li key={i}>• {s}</li>)}
                  </ul>
                </div>
                <div className="bg-white p-3 rounded-lg border border-red-200">
                  <h3 className="font-bold text-red-700 text-sm mb-2">↑ Areas to Improve</h3>
                  <ul className="text-xs text-gray-700 space-y-1">
                    {(session.final_report.key_weaknesses || []).map((s, i) => <li key={i}>• {s}</li>)}
                  </ul>
                </div>
              </div>
              <div className="bg-white p-3 rounded-lg border border-indigo-200">
                <h3 className="font-bold text-indigo-700 text-sm mb-1">💡 Personalised Advice</h3>
                <p className="text-xs text-gray-700">{session.final_report.advice}</p>
              </div>
              <div className="flex flex-wrap gap-2 justify-center mt-4">
                <Link to="/ai-interview"><Button className="bg-indigo-600 hover:bg-indigo-700" data-testid="another-interview-btn">Take Another Interview</Button></Link>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Answer Input */}
        {isWaitingAnswer && (
          <Card className="border-2 border-indigo-200" data-testid="answer-card">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm font-medium text-gray-700">Your Answer</p>
                {supported && (
                  <Button
                    type="button"
                    variant={listening ? 'default' : 'outline'}
                    onClick={toggle}
                    className={listening ? 'bg-red-600 hover:bg-red-700 text-white' : ''}
                    data-testid="mic-btn"
                  >
                    {listening ? <><MicOff size={16} className="mr-1" /> Stop</> : <><Mic size={16} className="mr-1" /> Speak</>}
                  </Button>
                )}
              </div>
              <Textarea
                value={answer}
                onChange={(e) => setAnswer(e.target.value)}
                placeholder={listening ? 'Listening… speak naturally.' : 'Type your answer, or click Speak to use voice…'}
                rows={5}
                className="mb-3 font-normal text-base"
                disabled={submitting}
                data-testid="answer-textarea"
              />
              <div className="flex items-center justify-between">
                <span className="text-xs text-gray-500">{answer.length} characters{listening && ' • 🔴 listening'}</span>
                <Button onClick={submit} disabled={submitting || !answer.trim()} className="bg-indigo-600 hover:bg-indigo-700" data-testid="submit-answer-btn">
                  {submitting ? <Loader2 className="animate-spin mr-2" size={16} /> : <Send size={16} className="mr-2" />}
                  Submit Answer
                </Button>
              </div>
              {!supported && <p className="text-xs text-gray-400 mt-2">💡 Voice input requires Chrome / Edge / Safari.</p>}
            </CardContent>
          </Card>
        )}
      </section>

      <Footer />
    </div>
  );
};

export default AIInterviewSession;
